import styles from './BoardColumn.module.scss';

import { Typography } from '@mui/material';
import { ColumnFiltersState, GroupingState, Row } from '@tanstack/react-table';
import React, { type JSX, useEffect, useMemo } from 'react';
import { useDrop } from 'react-dnd';
import { Subject } from 'rxjs';

import { VirtualizedTableV8 } from '@work4all/components/lib/dataDisplay/basic-table/VirtualizedTableV8';

import { Filters } from '@work4all/models/lib/DataProvider';
import { ICustomCellConfigBase } from '@work4all/models/lib/table-schema/table-schema';

import { useOpenMask } from '../../../../../containers/mask-overlays/mask-overlay/hooks/use-open-mask';
import { IUseEntityDataOptions } from '../../../entity-table/use-entity-data-table';
import { useEntityDataTableV8 } from '../../../entity-table/use-entity-data-table-v8';
import { useTableHandlersV8 } from '../../../use-table-handlers-v8';

import { BoardItem } from './BoardItem';
import { DragItem } from './types';

interface BoardColumnProps {
  status: string;
  prefilter: Filters;
  onDrop: (item: unknown) => void;
  columnFilters: ColumnFiltersState;
  grouping: GroupingState;
  tableProps: IUseEntityDataOptions<
    unknown,
    Record<string, ICustomCellConfigBase>
  >;
}

export const BoardColumn: React.FC<BoardColumnProps> = ({
  status,
  prefilter,
  columnFilters,
  grouping,
  onDrop,
  tableProps,
}) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'BOARD_ITEM',
    drop: (item: DragItem) => onDrop(item.item),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  const dataTable = useTableKanbanColumn(
    prefilter,
    columnFilters,
    grouping,
    tableProps,
    (selectedRows) => {
      const idRows = selectedRows as Row<{ id: number }>[];
      updateState({
        ids: idRows.map((x) => x.original.id),
        status,
      });
    }
  );

  const cardConfig = useMemo(() => {
    return {
      ...dataTable.cardConfig,
      secondaryIcons: [],
      wrapper: BoardItem,
    };
  }, [dataTable.cardConfig]);

  // Remove selected if status is different
  useEffect(() => {
    const subscription = BoardIdObserver.subscribe((event) => {
      if (!event) return;

      if (event.status === status || !event.ids.length) return;
      dataTable.table.toggleAllRowsSelected(false);
    });
    return () => subscription.unsubscribe();
  }, [status, dataTable.table]);

  return (
    <div
      ref={(node) => {
        drop(node);
      }}
      className={styles.board}
      style={{
        backgroundColor: isOver ? 'var(--ui03)' : undefined,
      }}
    >
      <Typography variant="caption" className={styles.header}>
        {status.toUpperCase()}
      </Typography>
      <div className={styles.boardContent}>
        <BoardColumnTableWrapper dataTable={dataTable}>
          {(tableHandlers) => (
            <VirtualizedTableV8
              {...dataTable}
              cardsView
              cardConfig={cardConfig}
              className={styles.table}
              classes={{
                row: styles.cardRow,
                tableBody: styles.tableBody,
              }}
              onRowDoubleClick={(id) => {
                const entityId = id.substring(id.lastIndexOf('.') + 1);
                tableHandlers.edit?.handler(entityId);
              }}
            />
          )}
        </BoardColumnTableWrapper>
      </div>
    </div>
  );
};

export const useTableKanbanColumn = (
  prefilter: Filters,
  columnFilters: ColumnFiltersState,
  grouping: GroupingState,
  tableProps: IUseEntityDataOptions<
    unknown,
    Record<string, ICustomCellConfigBase>
  >,
  onRowsSelected: (rows: Row<unknown>[]) => void
) => {
  const dataTable = useEntityDataTableV8({
    ...tableProps,
    prefilter,
    defaultColumnFilters: columnFilters,
    defaultGrouping: grouping,
    pageSize: 20,
    enableURLState: false,
    enableMultiRowSelection: false,
    onRowsSelected,
  });

  // Update filters and grouping for this board column when parent table state changes
  useEffect(() => {
    const tableState = dataTable.table.getState();

    if (tableState.grouping !== grouping) {
      dataTable.table.setGrouping(grouping);
    }

    if (tableState.columnFilters !== columnFilters) {
      dataTable.table.setColumnFilters(columnFilters);
    }
  }, [dataTable.table, grouping, columnFilters]);

  return dataTable;
};

export const BoardIdObserver = new Subject<{
  ids: number[];
  status: string;
}>();
const updateState = (newState) => {
  BoardIdObserver.next(newState);
};

interface BoardColumnTableWrapperProps {
  children: (
    tableHandlers: ReturnType<typeof useTableHandlersV8>
  ) => JSX.Element;
  dataTable: ReturnType<typeof useTableKanbanColumn>;
}

const BoardColumnTableWrapper = ({
  children,
  dataTable,
}: BoardColumnTableWrapperProps) => {
  const onOpenMask = useOpenMask({ entityType: dataTable.entityType });
  const tableHandlers = useTableHandlersV8(
    dataTable.table,
    dataTable.entityType,
    onOpenMask
  );

  return children(tableHandlers);
};
